import { analyticsAction } from "~/telemetry/product-analytics.server"
// Share lists API. GET tells the editor who is sharing: signed in or not, their handle, or a suggested one.
// POST writes: create, update, set visibility, delete, and undo a recent delete. The signed-in person must own the list.
import {
	type ActionFunctionArgs,
	json,
	type LoaderFunctionArgs,
} from "@remix-run/node"
import { getCachedProfileByUserId } from "~/server/share-lists/view.server"
import { useMutation, useQuery } from "@tanstack/react-query"
import { warmShareCard } from "~/server/share-card/images.server"
import {
	createList,
	deleteList,
	type ShareList,
	ShareListError,
	type ShareListInput,
	restoreList,
	setListVisibility,
	suggestHandle,
	updateList,
	type Visibility,
} from "~/server/share-lists/store.server"
import { getUserFromRequest, getUserIdFromRequest } from "~/utils/auth"

type Body =
	| { intent: "create"; list: ShareListInput }
	| { intent: "update"; id: string; list: ShareListInput }
	| { intent: "visibility"; id: string; visibility: Visibility }
	| { intent: "delete"; id: string }
	| { intent: "restore"; id: string }

export type ShareListResponse =
	| { list: ShareList }
	| { deleted: true }
	| { error: string; code?: "handle_required" }

/** Who is sharing, and their handle. Without one, a free suggestion from the account's name or email address. */
export type ShareViewer =
	| { signedIn: false }
	| { signedIn: true; handle: string | null; suggestedHandle: string | null }

export async function loader({ request }: LoaderFunctionArgs) {
	const noStore = { headers: { "Cache-Control": "private, no-store" } }
	const user = await getUserFromRequest({ request })
	if (!user) return json<ShareViewer>({ signedIn: false }, noStore)
	const profile = await getCachedProfileByUserId(user.id)
	if (profile)
		return json<ShareViewer>(
			{ signedIn: true, handle: profile.handle, suggestedHandle: null },
			noStore,
		)
	const meta = user.user_metadata ?? {}
	const suggestedHandle = await suggestHandle(user.id, [
		meta.user_name,
		meta.preferred_username,
		meta.full_name,
		meta.name,
		user.email?.split("@")[0],
	])
	return json<ShareViewer>(
		{ signedIn: true, handle: null, suggestedHandle },
		noStore,
	)
}

async function productAction({ request }: ActionFunctionArgs) {
	if (request.method !== "POST") return json({ error: "Method not allowed" }, { status: 405 })
	const userId = await getUserIdFromRequest({ request })
	if (!userId) return json({ error: "Sign in to save and share lists." }, { status: 401 })

	let body: Body
	try {
		body = await request.json()
	} catch {
		return json({ error: "Invalid request." }, { status: 400 })
	}
	const hasList = (b: Body) =>
		b.intent === "create" || b.intent === "update" ? typeof b.list === "object" && b.list !== null : true
	if (typeof body !== "object" || body === null || typeof body.intent !== "string" || !hasList(body)) {
		return json({ error: "Invalid request." }, { status: 400 })
	}

	try {
		switch (body.intent) {
			case "create": {
				const list = await createList(userId, body.list)
				warmShareCard(list)
				return json({ list })
			}
			case "update": {
				const list = await updateList(userId, String(body.id), body.list)
				warmShareCard(list)
				return json({ list })
			}
			case "visibility":
				return json({
					list: await setListVisibility(
						userId,
						String(body.id),
						body.visibility,
					),
				})
			case "delete":
				await deleteList(userId, String(body.id))
				return json({ deleted: true })
			case "restore":
				return json({ list: await restoreList(userId, String(body.id)) })
			default:
				return json({ error: "Unknown request." }, { status: 400 })
		}
	} catch (error) {
		if (error instanceof ShareListError)
			return json(
				{ error: error.message, code: error.code },
				{ status: error.status },
			)
		console.error("[share-lists] write failed", error)
		return json({ error: "Saving failed. Try again." }, { status: 500 })
	}
}

// Query and mutation helpers

/** A failed share-list request. `code` is "handle_required" when the account has no handle yet. */
export class ShareListRequestError extends Error {
	constructor(
		message: string,
		readonly code?: "handle_required",
	) {
		super(message)
	}
}

export const shareViewerQueryKey = ["share-viewer"]

export const fetchShareViewer = async (): Promise<ShareViewer> => {
	const response = await fetch("/api/share-lists")
	if (!response.ok) throw new Error("Couldn't check your account. Try again.")
	return response.json()
}

/** The signed-in person's handle, or a suggestion when they have none yet. Pass enabled: false for guests. */
export const useShareViewer = (enabled: boolean) =>
	useQuery<ShareViewer>({
		queryKey: shareViewerQueryKey,
		queryFn: fetchShareViewer,
		enabled,
	})

export const useCreateShareList = () =>
	useMutation({
		mutationFn: async (list: ShareListInput): Promise<ShareList> => {
			const response = await fetch("/api/share-lists", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ intent: "create", list }),
			})
			const result = await response.json().catch(() => ({}))
			if (!response.ok)
				throw new ShareListRequestError(
					result.error ?? "Saving the list failed. Try again.",
					result.code,
				)
			return result.list
		},
	})

/** Owner actions on a saved list: change its visibility, delete it, or undo a recent delete. */
export type ShareListOwnerAction =
	| { intent: "visibility"; id: string; visibility: Visibility }
	| { intent: "delete"; id: string }
	| { intent: "restore"; id: string }

export const useShareListOwnerAction = () =>
	useMutation({
		mutationFn: async (
			action: ShareListOwnerAction,
		): Promise<ShareListResponse> => {
			const response = await fetch("/api/share-lists", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(action),
			})
			const result = await response.json().catch(() => ({}))
			if (!response.ok)
				throw new ShareListRequestError(
					result.error ?? "That didn't work. Try again.",
					result.code,
				)
			return result
		},
	})

export const action = analyticsAction(productAction)
